import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router';
import { PARAMETERS } from '@root/tracking/constants';
import { Card, OnlyInMobile, ExpandableDescription, VideoPlayer } from '@components/shared';
import { Row, Col } from '@components/antd';
import routes from '@root/routes';
import { Landlord } from '@root/types';
import {
  galleryNavigated,
  landlordThumbnailClicked,
  savedBuildingOwnerNotificationLinkClicked,
} from '@store/actions/landlordPage';
import rootActions from '@store/actions';
import useMarketo from '@root/shared/useMarketo';
import useAnalytics from '@shared/useAnalytics';
import { get } from '@shared/typedApi';
import { useQuery } from '@tanstack/react-query';
import NotFound from '../404/NotFound';
import Portfolio from './Portfolio';
import Layout from '../Layout';
import LandlordHeaderSection from './LandlordHeaderSection';
import RawImages from '../Building/Images/RawImages';
import LandlordHead from './LandlordHead';
import s from './Landlord.module.less';
import LandlordSkeleton from './LandlordSkeleton';

const mapDispatch = {
  savedBuilingOwnerLinkClicked: () => savedBuildingOwnerNotificationLinkClicked,
};

const connector = connect(null, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

export const RawLandlord = ({ savedBuilingOwnerLinkClicked }: ReduxProps) => {
  const { t } = useTranslation('landlord');
  const dispatch = useDispatch();
  const { slug } = useParams<{ slug: string }>();
  const { pageLoaded } = useAnalytics();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const {
    data: landlord,
    isLoading,
    error,
  } = useQuery<Landlord>([routes.api.landlord(slug)], () => get(routes.api.landlord(slug)), {
    onSuccess: async fetchedLandlord => {
      dispatch(rootActions.setLandlordPage(fetchedLandlord.analyticsInformation));
      if (!hasLoadedOnce) {
        pageLoaded({
          pageName: PARAMETERS.landlordPage,
          actionType: 'PAGE_LOADED:LANDLORD',
        });
        setHasLoadedOnce(true);
      }
      window.prerenderReady = true;
    },
  });

  useMarketo();

  if (isLoading) {
    return (
      <Layout>
        <LandlordSkeleton />
      </Layout>
    );
  }

  if (error) {
    return <NotFound />;
  }

  return landlord ? (
    <Layout showFooter={!!landlord}>
      <LandlordHead landlord={landlord} />
      <LandlordHeaderSection
        landlord={landlord}
        savedBuilingOwnerLinkClicked={savedBuilingOwnerLinkClicked}
        hasLoadedOnce={hasLoadedOnce}
      />
      <div className={s.mediaAssets}>
        <RawImages
          photos={landlord.photos}
          video={landlord.video}
          lightboxPaginationClicked={() => dispatch(galleryNavigated)}
          lightboxThumbnailClicked={() => dispatch(landlordThumbnailClicked)}
          sourcePage={PARAMETERS.landlordPage}
          sourceContent={PARAMETERS.brandGallery}
          firstPhotoOrientation={landlord.firstPhotoOrientation}
          imagesZipUrl={routes.api.landlordImagesZipUrl(landlord.slug)}
          companyName={landlord.name}
        />
      </div>
      <Row gutter={16}>
        <Col lg={16} md={16} sm={24}>
          {/* eslint-disable react/no-danger */}
          <ExpandableDescription>
            <div
              className={s.description}
              dangerouslySetInnerHTML={{ __html: landlord.description }}
            />
          </ExpandableDescription>
          {/* eslint-enable react/no-danger */}
          {landlord.video ? (
            <OnlyInMobile>
              <VideoPlayer
                video={landlord.video}
                className={s.mobileVideo}
                sourcePage={PARAMETERS.landlordPage}
                sourceContent={PARAMETERS.spotlightTour}
                testId="mobileVideo"
              />
            </OnlyInMobile>
          ) : null}
        </Col>
        {landlord.executives && landlord.executives.length > 0 ? (
          <Col className={s.cardColumn} lg={8} md={8} sm={24}>
            <Card title={t('keyExecutives')}>
              <ul className={s.keyExecutiveList}>
                {landlord.executives.map(executive => (
                  <li key={executive}>{executive}</li>
                ))}
              </ul>
            </Card>
          </Col>
        ) : null}
      </Row>
      <Portfolio landlordSlug={landlord.slug} marketSlugs={landlord.marketSlugs} />
    </Layout>
  ) : (
    <NotFound />
  );
};

export default connector(RawLandlord);
